import * as B from '@blueprintjs/core';
import * as I from 'immutable';
import React from 'react';
import Gravatar from 'react-gravatar';

import {usePushNotification} from 'app/components/Notification';
import {ApiOrganizationUser} from 'app/modules/Remote/Organization';
import {useOrgUsers} from 'app/providers/OrgUsersProvider';
import {recordEvent} from 'app/tools/Analytics';
import {loginAppUrl} from 'app/utils/environmentUtils';
import {useStateWithDeps} from 'app/utils/hookUtils';

import * as GlobalSettings from './GlobalSettings';
import cs from './TeamMemberModal.styl';

export const SelfProfileView: React.FunctionComponent<
  React.PropsWithChildren<{
    profile: I.ImmutableOf<ApiOrganizationUser>;
  }>
> = ({profile}) => {
  const profileJs = React.useMemo(() => profile.toJS(), [profile]);
  const [isUpdating, setIsUpdating] = React.useState(false);
  const pushNotification = usePushNotification();
  const {actions} = useOrgUsers();

  const [nameField, setNameField] = useStateWithDeps<string | undefined>(profileJs.name, [
    profileJs,
  ]);

  const onInputBlur = React.useCallback(async () => {
    const name = profileJs.name;

    // Do nothing if we dont have a ref, or our value has not changed.
    if (!nameField || !nameField.trim() || nameField === name) {
      return;
    }

    try {
      setIsUpdating(true);
      await actions.setUserName(profileJs.id, nameField);
      recordEvent('Set user name');
      pushNotification({
        message: `User name updated.`,
        autoHideDuration: 3000,
        options: {
          intent: B.Intent.SUCCESS,
        },
      });
    } catch (_e) {
      // Reset input value and inform of failure
      setNameField(name);
      pushNotification({
        message: 'There was a problem updating your name. Please try again.',
        autoHideDuration: 3000,
        options: {
          intent: B.Intent.DANGER,
        },
      });
    } finally {
      setIsUpdating(false);
    }
  }, [actions, nameField, profileJs.id, profileJs.name, pushNotification, setNameField]);

  return (
    <GlobalSettings.Body title="Profile">
      <div className={cs.section}>
        <div className={cs.sectionHead}>
          <h1>User Details</h1>
        </div>
        <div className={cs.sectionControls}>
          <div className={cs.stacked}>
            <div className={cs.field}>
              <label>Photo</label>
              <Gravatar email={profileJs.email} />
            </div>
            <div className={cs.inline}>
              <div className={cs.field}>
                <label htmlFor="name-input">Name</label>
                <B.InputGroup
                  id="name-input"
                  disabled={isUpdating}
                  value={nameField}
                  onValueChange={(val) => setNameField(val)}
                  onBlur={onInputBlur}
                />
              </div>
              <div className={cs.field}>
                <label htmlFor="email">Email</label>
                <div className={cs.email}>{profileJs.email}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={cs.section}>
        <div className={cs.sectionHead}>
          <h1>Authentication</h1>
        </div>
        <div className={cs.sectionControls}>
          <div className={cs.stacked}>
            <div>
              <B.AnchorButton
                text="Update Email"
                onClick={() => {
                  // Open login app Update Email in new tab
                  window.open(
                    `${loginAppUrl}/updateEmail?redirect=${window.location.href}`,
                    '_blank'
                  );
                }}
              />
            </div>
            <div>
              <B.AnchorButton
                text="Configure Multi-Factor Authentication"
                onClick={() => {
                  // Open login app MFA Settings in new tab
                  window.open(
                    `${loginAppUrl}/mfaSettings?redirect=${window.location.href}`,
                    '_blank'
                  );
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </GlobalSettings.Body>
  );
};
